import { useEffect, useRef, useState } from "react";
import { usePathname } from "@/lib/navigation";
import { useAuth } from "@/hooks/useAuth";
import { announcementsApi, type Announcement } from "@/lib/api";
import { AnnouncementDialog } from "./AnnouncementDialog";

// Auth screens where the dialog must never appear; anywhere else is real app content.
const AUTH_SCREENS = ["/login"];

/**
 * Mounted once globally (in Providers). After the auth flow completes it fetches
 * the current user's unseen announcements and pages through them in a dialog,
 * marking each seen as the user advances. Dismissing marks the rest seen too.
 *
 * Fetches once per user id (re-fetching when a different user signs in). No
 * sessionStorage guard: this component lives in Providers, which does NOT
 * remount on client navigation, and the server-side seen-state is the durable
 * "show once" — so re-checking on each fresh mount/login is correct and cheap.
 */
export function AnnouncementGate() {
  const { user, loading } = useAuth();
  const pathname = usePathname();
  const [items, setItems] = useState<Announcement[]>([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const fetchedForUser = useRef<string | null>(null);

  // Eligible once the user is on real app content, not the login screen.
  const onAuthScreen = AUTH_SCREENS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
  const eligible = !loading && !!user && !onAuthScreen;

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    // Logged out / not ready: drop any open dialog and clear the guard so the
    // NEXT login (even a different account in the same tab) fetches fresh.
    if (!eligible || !user) {
      fetchedForUser.current = null;
      setOpen(false);
      return;
    }
    // Already handled this user — don't refetch on unrelated re-renders.
    if (fetchedForUser.current === user.id) return;
    fetchedForUser.current = user.id;

    void (async () => {
      try {
        const unseen = await announcementsApi.listUnseen();
        // Guard against a stale in-flight response if the user switched mid-fetch.
        if (fetchedForUser.current !== user.id) return;
        if (unseen.length > 0) {
          setItems(unseen);
          setIndex(0);
          setOpen(true);
        }
      } catch {
        // Announcements are non-critical — never block the app on a failure.
        // Allow a retry on the next auth change.
        fetchedForUser.current = null;
      }
    })();
  }, [eligible, user]);
  /* eslint-enable react-hooks/set-state-in-effect */

  function advance() {
    const current = items[index];
    if (current) void announcementsApi.markSeen(current.id).catch(() => {});
    if (index < items.length - 1) {
      setIndex(index + 1);
    } else {
      setOpen(false);
    }
  }

  function dismiss() {
    // "Dismiss and it won't come back" — mark every loaded item seen.
    for (const item of items) void announcementsApi.markSeen(item.id).catch(() => {});
    setOpen(false);
  }

  const current = items[index];
  if (!current) return null;

  return (
    <AnnouncementDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss();
      }}
      announcement={current}
      index={index}
      total={items.length}
      onNext={advance}
    />
  );
}
